/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/jsx-props-no-spreading */
import { Modal } from 'react-bootstrap';
import RadioButtonGroup from '@mui/material/RadioGroup';
import { useState } from 'react';
import { browserHistory } from 'react-router';
import * as _ from 'lodash';
import Dropzone from 'react-dropzone';
import Button, { BUTTON_SIZE, BUTTON_TYPE, ButtonGroup, IconButton, InlineLoaderButton } from '../../generic/Button';
import { AsyncSelect, FormLabelVariant, Icon, icons, LabeledRadio, LabeledTextInput } from '../../generic';
import type { SelectOption } from '../../../types';
import styles from './DisplayProjectNav.module.less';
import { useDebouncedCallback } from '../../../hooks';
import { get } from '../../../redux/api';
import { useParams } from '../../../contexts/RouterContext';
import Locator from '../../../../shared/Locator';
import FormLabel from '../../generic/Forms/FormLabel';
import type { ProjectRevisions } from '../../../../typings/api';
import { FileType } from '../../../../shared/fileUpload';
import { archiveUploadAccept, nuc, sbomUploadAccept } from '../../../utils';
import ProgressBar from '../../generic/ProgressBar';
import { useFileUpload } from './hooks';

type Props = {
  onHide: () => void;
  shouldShow: boolean;
  fileType: FileType;
};

type Params = {
  project_id: string;
  reference_type: 'branch' | 'tag';
  reference: string;
  revision_id: string;
};

/**
 * Create the payload for the revisions endpoint to return 10 revisions
 * for the given search string, reference, and reference type.
 */
const createRevisionsEndpointPayload = ({
  search,
  reference,
  referenceType,
}: {
  search: string;
  reference: string;
  referenceType: Params['reference_type'];
}) => {
  return {
    locator: search,
    count: 10,
    offset: 0,
    refs: [reference],
    refs_type: referenceType,
  };
};

const ModalUploadSBOM = ({ onHide, shouldShow, fileType }: Props) => {
  const routerParams = useParams<Params>();
  const [isCreatingNewSBOM, setIsCreatingNewSBOM] = useState(true);
  const versionButtonValue: 'NEW' | 'REPLACE' = isCreatingNewSBOM ? 'NEW' : 'REPLACE';
  const [sbomVersion, setSBOMVersion] = useState('');
  const isSBOM = fileType === FileType.SBOM;
  const acceptExtensions = isSBOM ? sbomUploadAccept : archiveUploadAccept;
  const { onDrop, size, file, resetFile, onUpload, isUploading } = useFileUpload(fileType);
  const onCancel = () => {
    resetFile();
    setSBOMVersion('');
    onHide();
  };
  const locator = new Locator(routerParams.project_id);
  const onUploadSBOM = async () => {
    await onUpload({ locator: routerParams.project_id, revision: sbomVersion, revisionType: versionButtonValue });
    onCancel();
    const basePath = `/projects/${nuc(routerParams.project_id)}`;
    const refsSuffix = `${routerParams.reference_type || 'branch'}/${routerParams.reference || 'master'}/${nuc(sbomVersion)}`;
    const targetPath = isCreatingNewSBOM
      ? `${basePath}/refs/${refsSuffix}`
      : `${basePath}/history/builds`;
    browserHistory.push(targetPath);
  };
  const [isRevisionNew, setIsRevisionNew] = useState(true);
  const shouldDisableSave = !file || !sbomVersion;
  const checkThatRevisionIsNew = useDebouncedCallback(async (revision: string) => {
    locator.revision = revision;
    try {
      const res = await get(`/api/revisions/${nuc(locator.toString())}/minimal`);
      setIsRevisionNew(res.status !== 200);
    } catch (error: unknown) {
      // `get` throws when the API returns 404, which means the revision does not exist
      setIsRevisionNew(true);
    }
  });

  return (
    <Modal
      onHide={() => {
        resetFile();
        onHide();
      }}
      show={shouldShow}
      className={styles.modalUploadSBOM}
    >
      <Modal.Header closeButton>
        <Modal.Title>Upload New SBOM</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.section}>
          <div className={styles.subtitle}>{file ? 'File' : 'New File'}</div>
          {file ? (
            <div className={styles.droppedFile}>
              <span className={styles.acceptedFileIcon}>
                <Icon icon={icons.receiptSolid} />
              </span>
              <span className={styles.fileName}>{file.file.customName || ''}</span>
              <span className={styles.fileSize}>{size}</span>
              <span className={styles.acceptedFileIcon}>
                <IconButton icon={icons.xMark} onClick={resetFile} id="dropzone-remove-file" />
              </span>
            </div>
          ) : (
            <Dropzone onDrop={onDrop} accept={acceptExtensions}>
              {
                ({ getRootProps, getInputProps }) => (
                  <div className={styles.dropZone} {...getRootProps()}>
                    <input {...getInputProps()} />
                    <span className={styles.dropZoneTopLine}>
                      Drop your file here or <span className={styles.pseudoLink}>browse local files</span>
                    </span>
                    <p>
                      <b>Accepted formats:</b> CycloneDX(XML/JSON) and SPDX(JSON)
                    </p>
                  </div>
                )
              }
            </Dropzone>
          )}
        </div>
        {!isUploading && (
          <div className={styles.section}>
            <div className={styles.subtitle}>Version</div>
            <RadioButtonGroup
              name="versionGroup"
              defaultValue="NEW"
              row
              value={versionButtonValue}
              onChange={(_e, val: string) => {
                if (val === 'NEW' && !isCreatingNewSBOM) {
                  setIsCreatingNewSBOM(true);
                  setSBOMVersion('');
                  setIsRevisionNew(true);
                } else if (val === 'REPLACE' && isCreatingNewSBOM) {
                  setIsCreatingNewSBOM(false);
                  if (sbomVersion && isRevisionNew) {
                    // Only unset the version string if it would represent a new version
                    // This keeps the version string if it matches an existing revision,
                    // making it easier for the user to switch to the replace tab to replace it.
                    setSBOMVersion('');
                  }
                }
              }}
            >
              <LabeledRadio label="Create a new version" value="NEW" />
              <LabeledRadio label="Replace an existing version" value="REPLACE" />
            </RadioButtonGroup>
            {isCreatingNewSBOM ? (
              <>
                <LabeledTextInput
                  labelVariant={FormLabelVariant.Filter}
                  label="Version"
                  placeholder={'examples "1.2.3" or "v2 Alpha"'}
                  value={sbomVersion}
                  spellCheck={false}
                  onChange={(val) => {
                    setSBOMVersion(val);
                    if (!_.isEmpty(val)) {
                      checkThatRevisionIsNew(val);
                    }
                  }}
                  isDisabled={!isCreatingNewSBOM}
                />
                {sbomVersion && !isRevisionNew ? (
                  <p className={styles.error}>
                    This version already exists. Would you like to{' '}
                    <Button type="anchor" onClick={() => setIsCreatingNewSBOM(false)}>
                      replace it
                    </Button>{' '}
                    instead?
                  </p>
                ) : (
                  <p className={styles.caption}>Give this version a unique number or description</p>
                )}
              </>
            ) : (
              <>
                <FormLabel label="Version to replace" htmlFor="replacement-version" variant={FormLabelVariant.Filter}>
                  <AsyncSelect
                    id="replacement-version"
                    value={{ label: sbomVersion, value: sbomVersion } as SelectOption}
                    loadOptions={async (inputValue: string) => {
                      const { data } = await get<ProjectRevisions>(
                        `/api/projects/${nuc(routerParams.project_id)}/revisions`,
                        false,
                        createRevisionsEndpointPayload({
                          search: inputValue,
                          reference: routerParams.reference,
                          referenceType: routerParams.reference_type,
                        }),
                      );
                      if (!data) {
                        return [];
                      }

                      return (data[routerParams.reference_type][routerParams.reference] || []).map((revision) => {
                        const revisionLocator = new Locator(revision.locator);
                        return {
                          label: revisionLocator.revision.value || '',
                          value: revisionLocator.revision.value || '',
                        };
                      });
                    }}
                    defaultOptions
                    onChange={(option) => {
                      if (option) {
                        setSBOMVersion(option.value);
                      }
                    }}
                  />
                </FormLabel>
                <p className={styles.caption}>Choose the version you wish to replace</p>
              </>
            )}
          </div>
        )}
        {isUploading && (
          <div className={styles.section}>
            <ProgressBar
              customMessage={`${isCreatingNewSBOM ? 'Creating' : 'Replacing'} ${sbomVersion}`}
              bytesFinished={file?.status.bytesUploaded || 0}
              bytesTotal={file?.file.size || 0}
              hasFailed={!!file?.status.errorMessage}
              errorMessage={file?.status.errorMessage}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button type={BUTTON_TYPE.SECONDARY} size={BUTTON_SIZE.LARGE} onClick={onCancel}>
            Cancel
          </Button>
          <InlineLoaderButton
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.LARGE}
            isSubmitButton
            isLoading={isUploading}
            isDisabled={shouldDisableSave || isUploading}
            onClick={onUploadSBOM}
          >
            Upload SBOM
          </InlineLoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalUploadSBOM;
